// ==============================================
// Admin Device Detail Page
// ==============================================

import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArchiveBoxIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { formatFullDateTime, formatRelativeTime } from '../../utils/formatters';
import { InlineRename } from '../../components/ui/InlineRename';
import DeviceOverviewTab from '../../components/devices/DeviceOverviewTab';
import { TileRouteCard } from '../../components/devices/TileRouteCard';
import DeviceTelemetryTab from '../../components/devices/DeviceTelemetryTab';
import DeviceHealthTab from '../../components/devices/DeviceHealthTab';
import DeviceLocationTab from '../../components/devices/DeviceLocationTab';
import DeviceCommandsTab from '../../components/devices/DeviceCommandsTab';
import DeviceDiagnosticsTab from '../../components/devices/DeviceDiagnosticsTab';
import DeviceAuditTab from '../../components/devices/DeviceAuditTab';
import DeviceAisTab from '../../components/devices/DeviceAisTab';
import DeviceSafetyTab from '../../components/devices/DeviceSafetyTab';

type TabId = 'overview' | 'telemetry' | 'health' | 'location' | 'ais' | 'commands' | 'diagnostics' | 'audit' | 'safety';

const tabs: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'telemetry', label: 'Telemetry' },
  { id: 'health', label: 'Health' },
  { id: 'location', label: 'Location' },
  { id: 'ais', label: 'AIS' },
  { id: 'commands', label: 'Commands' },
  { id: 'diagnostics', label: 'Diagnostics' },
  { id: 'audit', label: 'Audit' },
  { id: 'safety', label: 'Safety' },
];

// What each sign-in rejection reason means for whoever is looking (#753).
const rejectionMeaning: Record<string, string> = {
  token_invalid: 'Its token is not one we issued. Usually a lost token: unlink it, then re-register it on the Scout.',
  token_expired: 'It is using an old token that has expired: unlink it, then re-register it on the Scout.',
  device_pending: 'It was unlinked and has not been re-registered with the new code.',
  device_suspended: 'This device is suspended.',
  device_revoked: 'This device was deleted (revoked) from its account.',
  device_unregistered: 'This record has never been registered.',
};

function isTabId(value: string | null): value is TabId {
  return tabs.some((t) => t.id === value);
}

const statusColors: Record<string, string> = {
  unregistered: 'bg-slate-500/20 text-slate-400',
  pending: 'bg-yellow-500/20 text-yellow-400',
  active: 'bg-green-500/20 text-green-400',
  suspended: 'bg-orange-500/20 text-orange-400',
  revoked: 'bg-red-500/20 text-red-400',
};

const statusIcons: Record<string, React.ReactNode> = {
  unregistered: <ArchiveBoxIcon className="w-4 h-4" />,
  pending: <ClockIcon className="w-4 h-4" />,
  active: <CheckCircleIcon className="w-4 h-4" />,
  suspended: <XCircleIcon className="w-4 h-4" />,
  revoked: <XCircleIcon className="w-4 h-4" />,
};

export function AdminDeviceDetailPage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // The tab lives in the URL (`?tab=diagnostics`) so a link can land on it.
  // A Diagnose report link (`?report=drp_…`, from the notification email)
  // implies the Diagnostics tab, which opens the report itself.
  const requestedTab = searchParams.get('tab');
  const activeTab: TabId = isTabId(requestedTab)
    ? requestedTab
    : searchParams.get('report')
      ? 'diagnostics'
      : 'overview';
  const setActiveTab = (tab: TabId) => {
    // Replace, not push: tab flips should not fill the Back stack. Keep the
    // history state, which carries the Back button's return address.
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true, state: location.state });
  };

  // Callers that reach a device from somewhere other than the device list
  // (e.g. the admin users page, via a user's expanded device row) hand us the
  // way back in history state. Direct URL visits have no state and fall back
  // to the device list, which is the historical behaviour.
  const backTo = (location.state as { from?: string } | null)?.from;
  const backLabel = backTo ? 'Back' : 'Back to Devices';
  // A return trip says so, so the page it lands on can put its scroll back
  // (Admin → Users does).
  const goBack = () => navigate(backTo ?? '/admin/devices', backTo ? { state: { restorePlace: true } } : undefined);

  const queryClient = useQueryClient();

  // Fetch device details
  const { data: device, isLoading: deviceLoading } = useQuery({
    queryKey: ['admin', 'device', deviceId],
    queryFn: () => api.adminGetDevice(deviceId!),
    enabled: !!deviceId,
    refetchInterval: 30000,
  });

  // Fetch latest telemetry (for overview tab status cards)
  const { data: telemetry } = useQuery({
    queryKey: ['device-telemetry', deviceId],
    queryFn: () => api.adminGetDeviceTelemetry(deviceId!),
    enabled: !!deviceId,
    refetchInterval: 60000,
  });

  if (deviceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400 text-lg">Device not found</p>
        <button
          onClick={goBack}
          className="mt-4 text-cyan-400 hover:text-cyan-300"
        >
          {backLabel}
        </button>
      </div>
    );
  }

  const isOnline = device.last_seen_at &&
    (Date.now() - new Date(device.last_seen_at).getTime()) < 5 * 60 * 1000;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={goBack}
            title={backLabel}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center">
              {device.device_type === 'scout' ? (
                <DevicePhoneMobileIcon className="w-6 h-6 text-cyan-400" />
              ) : (
                <ServerIcon className="w-6 h-6 text-purple-400" />
              )}
            </div>
            <div>
              <InlineRename
                value={device.name || device.serial_number}
                onSave={async (name) => {
                  await api.updateDevice(device.id, { name });
                  await queryClient.invalidateQueries({ queryKey: ['admin', 'device', deviceId] });
                }}
                label="Rename device"
                hint={device.device_type === 'scout' ? 'Also renames the boat on the Scout and in emails.' : undefined}
                display={(name) => <h1 className="text-2xl font-bold text-white">{name}</h1>}
              />
              <p className="text-slate-400 text-sm">{device.serial_number}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Online indicator */}
          <span className={`flex items-center gap-1.5 text-sm ${isOnline ? 'text-green-400' : 'text-slate-500'}`}>
            <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-slate-500'}`} />
            {isOnline ? 'Online' : 'Offline'}
          </span>
          {/* Status badge */}
          <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-lg ${statusColors[device.status] || statusColors.unregistered}`}>
            {statusIcons[device.status]}
            {device.status}
          </span>
        </div>
      </div>

      {/* Sign-in rejections: a Scout turned away looks merely offline (#753). */}
      {!!device.auth_failure_count && device.auth_failure_count > 0 && (
        <div
          role="status"
          aria-label="Sign-in rejections"
          className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4"
        >
          <ExclamationTriangleIcon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-red-300">
              Turned away at sign-in {device.auth_failure_count} {device.auth_failure_count === 1 ? 'time' : 'times'} since it was last let in
              {device.last_auth_failure_at && (
                <span className="font-normal text-slate-400" title={formatFullDateTime(device.last_auth_failure_at)}>
                  {' '}· latest {formatRelativeTime(device.last_auth_failure_at)}
                </span>
              )}
            </p>
            <p className="text-slate-300 mt-1">
              {rejectionMeaning[device.last_auth_failure_reason ?? ''] ?? `Reason: ${device.last_auth_failure_reason ?? 'unknown'}.`}
            </p>
          </div>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Last Seen</p>
          <p className="text-lg font-semibold text-white">
            {device.last_seen_at
              ? new Date(device.last_seen_at).toLocaleString('en-NZ')
              : 'Never'}
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Subscription</p>
          <p className="text-lg font-semibold text-white">
            {device.user ? 'Active' : 'None'}
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">LA Version</p>
          <p className="text-lg font-semibold text-white">
            {telemetry?.la_version || device.la_version || '—'}
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Disk Usage</p>
          <p className="text-lg font-semibold text-white">
            {telemetry?.disk_used_mb && telemetry?.disk_total_mb
              ? `${Math.round((telemetry.disk_used_mb / telemetry.disk_total_mb) * 100)}%`
              : '—'}
          </p>
          {telemetry?.disk_used_mb && telemetry?.disk_total_mb && (
            <p className="text-xs text-slate-400">
              {(telemetry.disk_used_mb / 1024).toFixed(1)} / {(telemetry.disk_total_mb / 1024).toFixed(1)} GB
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700 overflow-x-auto">
        <nav className="flex gap-0 whitespace-nowrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-400'
                  : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <>
            <TileRouteCard deviceId={deviceId!} metadata={device.metadata} />
            <DeviceOverviewTab device={device} telemetry={telemetry} />
          </>
        )}
        {activeTab === 'telemetry' && (
          <DeviceTelemetryTab deviceId={deviceId!} />
        )}
        {activeTab === 'health' && (
          <DeviceHealthTab
            deviceId={deviceId!}
            device={
              device
                ? {
                    name: device.name,
                    serial_number: device.serial_number,
                    la_version: device.la_version,
                    last_seen_at: device.last_seen_at,
                  }
                : null
            }
          />
        )}
        {activeTab === 'location' && (
          <DeviceLocationTab device={device} deviceId={deviceId!} />
        )}
        {activeTab === 'ais' && (
          <DeviceAisTab deviceId={deviceId!} />
        )}
        {activeTab === 'commands' && (
          <DeviceCommandsTab deviceId={deviceId!} />
        )}
        {activeTab === 'diagnostics' && (
          <DeviceDiagnosticsTab deviceId={deviceId!} />
        )}
        {activeTab === 'audit' && (
          <DeviceAuditTab deviceId={deviceId!} />
        )}
        {activeTab === 'safety' && (
          <DeviceSafetyTab deviceId={deviceId!} />
        )}
      </div>
    </div>
  );
}

export default AdminDeviceDetailPage;
